import "typeface-roboto";
import "../bootstrap";
import "../style.css";

import CssBaseline from "@mui/material/CssBaseline";
import type { Theme as MuiTheme } from "@mui/material/styles";
import {
  createTheme,
  darken,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { AppProps } from "next/app";
import Head from "next/head";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Api, ApiProvider } from "../api";
import { AvatarOfFrost } from "../components/AvatarOfFrost";
import { createErrorBoundary } from "../components/createErrorBoundary";
import { LoadingBlock } from "../components/LoadingBlock";
import { assertNever, noop } from "../lib/utils";
import { RefreshErrorHandler } from "../scenes";
import { ConfirmDialogProvider } from "../services";
import { useStoredApiToken } from "../store/apiToken";
import { Theme, useStoredTheme } from "../store/theme";

const themeDark = createTheme({
  cssVariables: true,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          zIndex: 1,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          "&.MuiFab-default": {
            "&$focusVisible": {
              backgroundColor: "hsl(195, 15%, 40%)",
            },
            "&:hover": {
              backgroundColor: "hsl(195, 15%, 40%)",
            },
            backgroundColor: "hsl(195, 15%, 30%)",
            color: "#FFF",

            "&.Mui-disabled": {
              backgroundColor: "hsl(195, 15%, 25%)",
              color: "#999",
              opacity: 0.5,

              "&:hover": {
                backgroundColor: "hsl(195, 15%, 25%)",
              },
            },
          },
        },
      },
    },
  },
  palette: {
    background: {
      default: "hsl(195, 25%, 3%)",
      paper: "hsl(195, 25%, 6%)",
    },
    primary: {
      contrastText: "#000",
      dark: "hsl(195, 15%, 50%)",
      light: "hsl(195, 50%, 85%)",
      main: "hsl(195, 30%, 75%)",
    },
    secondary: {
      contrastText: "#FFF",
      dark: "hsl(25, 25%, 40%)",
      light: "hsl(25, 25%, 50%)",
      main: "hsl(25, 25%, 45%)",
    },
    error: {
      contrastText: "#FFF",
      dark: "hsl(0, 64.8%, 55.5%)",
      light: "hsl(0, 68.6%, 72.5%)",
      main: "hsl(4.3, 89.3%, 63.3%)",
    },
    success: {
      contrastText: "#FFF",
      dark: "hsl(122.7, 38.2%, 54.3%)",
      light: "hsl(122.7, 38.2%, 54.3%)",
      main: "hsl(123.3, 38.7%, 46.7%)",
    },
    mode: "dark",
  },
});

const themeLight = createTheme({
  cssVariables: true,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "hsl(195, 15%, 90%)",
          zIndex: 1,
        },
      },
    },
  },
  palette: {
    background: {
      default: "hsl(195, 15%, 95%)",
      paper: "hsl(195, 15%, 98%)",
    },
    primary: {
      contrastText: "#FFF",
      dark: "hsl(200, 50%, 30%)",
      light: "hsl(200, 50%, 65%)",
      main: "hsl(200, 50%, 50%)",
    },
    secondary: {
      contrastText: "#FFF",
      dark: "hsl(35, 50%, 40%)",
      light: "hsl(35, 50%, 60%)",
      main: "hsl(35, 50%, 50%)",
    },
    mode: "light",
  },
});

function useTheme(theme: Theme): MuiTheme {
  const preferDarkTheme = useMediaQuery("@media (prefers-color-scheme: dark)");

  switch (theme) {
    case Theme.System:
      return preferDarkTheme ? themeDark : themeLight;
    case Theme.Dark:
      return themeDark;
    case Theme.Light:
      return themeLight;
    default:
      return assertNever(theme);
  }
}

const ErrorBoundary = createErrorBoundary();

export default function MyApp({ Component, pageProps }: AppProps) {
  const [selectedTheme] = useStoredTheme();
  const theme = useTheme(selectedTheme);
  const [apiToken] = useStoredApiToken();
  const api = useMemo(() => {
    if (typeof apiToken !== "undefined") {
      return new Api({
        apiKey: apiToken,
        basePath: CONFIG.apiBaseUrl,
      });
    } else {
      return new Api({ basePath: CONFIG.apiBaseUrl });
    }
  }, [apiToken]);

  const themeColor = darken(theme.palette.background.default, 0.2);

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const body = document.body;

    if (theme.palette.mode === "dark") {
      body.classList.add("dark");

      return () => {
        body.classList.remove("dark");
      };
    } else {
      return noop;
    }
  }, [theme.palette.mode]);

  return (
    <>
      <Head>
        <title>Skylords Reborn</title>
        <meta
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
          name="viewport"
        />
        <meta content={theme.palette.mode} name="color-scheme" />
        <meta content={themeColor} name="theme-color" />
      </Head>
      <ErrorBoundary errorComponent={RefreshErrorHandler}>
        <ApiProvider value={api}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <AvatarOfFrost className="background" />
              {isClient ? (
                <Suspense fallback={<LoadingBlock />}>
                  <ConfirmDialogProvider>
                    <Component {...pageProps} />
                  </ConfirmDialogProvider>
                </Suspense>
              ) : (
                <LoadingBlock />
              )}
            </ThemeProvider>
          </StyledEngineProvider>
        </ApiProvider>
      </ErrorBoundary>
    </>
  );
}
